import { useRef, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { ButtonToolbar } from 'reactstrap';

import Button from 'src/componentes/button';
import IconDialog from 'src/componentes/icon-dialog';
import Checkbox from 'src/componentes/checkbox';
import ValidacaoRestricaoLista from 'src/componentes/validacao-restricao-lista/ValidacaoRestricaoLista';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import { TriangleAlert } from 'lucide-react';

const CriticalConfirm = ({
  handleConfirm,
  handleCancel,
  isFunction,
  title,
  message,
  labelCancel,
  labelConfirm,
  labelCheck,
  validationEndpoint = null,
  validationData = null,
  items = null,
  isLoading = false,
  canDelete = true,
  width = 500,
  ...rest
}) => {
  const intl = useIntl();
  const ref = useRef();
  const [checked, setChecked] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);
  const [internalItems, setInternalItems] = useState(null);
  const [internalCanDelete, setInternalCanDelete] = useState(true);

  const dynamicWidth =
    internalItems && internalItems.length > 0 ? Math.max(width, 700) : width;

  useEffect(() => {
    if (validationEndpoint && validationData) {
      setInternalLoading(true);

      http
        .post(validationEndpoint, validationData)
        .then((response) => {
          const restricoes = response.data;

          const temRestricoesReais =
            restricoes.restricoes &&
            restricoes.restricoes.length > 0 &&
            restricoes.restricoes.some(
              (restricao) =>
                restricao.restricaoDescricao &&
                !restricao.restricaoDescricao.includes(
                  'não possui restrição para exclusão'
                )
            );

          if (temRestricoesReais) {
            setInternalItems(restricoes.restricoes);
            setInternalCanDelete(restricoes.podeExcluir);
          } else {
            setInternalItems(null);
            setInternalCanDelete(restricoes.podeExcluir);
          }
        })
        .catch((error) => {
          errorHandler(error);
          setInternalItems(null);
          setInternalCanDelete(true);
        })
        .finally(() => {
          setInternalLoading(false);
        });
    }
  }, [validationEndpoint, validationData]);

  const hasItems =
    internalItems !== null
      ? internalItems.length > 0
      : items && items.length > 0;
  const finalItems = internalItems !== null ? internalItems : items;
  const finalCanDelete = internalItems !== null ? internalCanDelete : canDelete;
  const finalLoading = internalLoading || isLoading;

  const handleChangeChecked = (value) => {
    setChecked(value);
  };

  const handleConfirmClick = () => {
    if (finalCanDelete && checked) {
      handleConfirm && handleConfirm();
      if (ref.current) ref.current.close();
    }
  };

  const handleCancelClick = () => {
    handleCancel && handleCancel();
    if (ref.current) ref.current.close();
  };

  const actions = [
    <ButtonToolbar key="actions">
      <Button onClick={handleCancelClick} color="secondary">
        {labelCancel
          ? labelCancel
          : intl.formatMessage({ id: 'label.cancelar' })}
      </Button>

      {/* Só mostra botão de confirmação se puder excluir */}
      {finalCanDelete && (
        <Button
          disabled={!checked}
          className="ml-2"
          autoFocus
          color="danger"
          onClick={handleConfirmClick}
        >
          {labelConfirm ? labelConfirm : intl.formatMessage({ id: 'excluir' })}
        </Button>
      )}
    </ButtonToolbar>,
  ];

  const TitleIcon = () => (
    <TriangleAlert
      style={{
        backgroundColor: '#FFEBEB',
        height: 40,
        width: 40,
        padding: '10px',
      }}
      className="text-danger rounded-circle d-flex justify-content-center align-items-center ml-2"
      size={20}
    />
  );

  // Renderiza o conteúdo
  const renderContent = () => {
    if (finalLoading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Carregando...</span>
          </div>
          <div className="mt-3 text-muted">
            {intl.formatMessage({ id: 'carregando' })}
          </div>
        </div>
      );
    }

    if (hasItems) {
      // Modo com itens/restrições
      return (
        <div>
          <ValidacaoRestricaoLista restricoes={finalItems} />

          {/* Checkbox só aparece se puder excluir */}
          {finalCanDelete && (
            <Checkbox
              className="mb-3 mt-3"
              model={{
                label: labelCheck
                  ? labelCheck
                  : intl.formatMessage({ id: 'cienteAcaoIrreversivel' }),
                value: checked,
                requestChange: (value) => handleChangeChecked(value),
              }}
            />
          )}
        </div>
      );
    }

    // Modo padrão
    return (
      <div>
        <div className="mb-3">{isFunction ? message() : message}</div>

        <Checkbox
          className="mb-3"
          model={{
            label: labelCheck
              ? labelCheck
              : intl.formatMessage({ id: 'cienteAcaoIrreversivel' }),
            value: checked,
            requestChange: (value) => handleChangeChecked(value),
          }}
        />
      </div>
    );
  };

  return (
    <IconDialog
      ref={ref}
      width={dynamicWidth}
      title={title}
      titleColor="text-danger"
      actions={actions}
      onRequestClose={handleCancelClick}
      padContent
      TitleIcon={TitleIcon}
      {...rest}
    >
      {renderContent()}
    </IconDialog>
  );
};

export default CriticalConfirm;
